import styled, {css} from 'styled-components';
import React, {FC} from 'react';

import {transition, paletteAlphas, fontWeights} from '../../../../lib/styleVariables';
import {HeaderMenuLeftItemWithSubMenuProps} from './headerMenuTypes';
import {paletteR4, fontSizesMixinsR4} from '../../../../lib/styleVariablesR4';
import {Icon, IconProps} from '../../../molecules/icon';
import {headerHeight} from './headerVariables';
import {buttonHeight} from '../../../molecules/buttonsR4';

export interface HeaderSubMenuCssProps {
  wrapperCss?: string;
}

interface HeaderSubMenuProps extends HeaderMenuLeftItemWithSubMenuProps, HeaderSubMenuCssProps {
  isOpen?: boolean;
  locationPath: string;
}

export const StyledSubMenuWrapper = styled.div<{$css?: string, singleCategory: boolean}>`
  padding: 40px 40px 48px 24px;
  background: ${paletteR4.white.base};
  box-shadow: 0px 0px 20px ${paletteAlphas.black10};
  border-radius: 15px;
  position: absolute;
  top: 100%;
  transition: ${transition.fast};
  opacity: 0;
  visibility: hidden;
  transform: translateY(${(headerHeight.desktop - buttonHeight) / 2}px) translateX(${props => (props.singleCategory ? '-40px' : '0px')});
  display: flex;
  cursor: auto;

  ${props => !props.singleCategory && css`
    left: 0;
  `}

  ${props => props.$css}
`;

const StyledSubCategory = styled.div`
  &:not(:last-child) {
    padding-right: 40px;
    border-right: 1px solid ${paletteR4.dark_blue.base};
  }

  &:not(:first-child) {
    margin-left: 24px;
  }
`;

const StyledSubCategoryTitle = styled.h5`
  ${fontSizesMixinsR4.h7()};
  text-transform: uppercase;
  margin: 0 0 27px 16px;
  color: ${paletteR4.red.lowlight};
`;

const StyledSubMenuItemTitle = styled.h6<{isActive: boolean}>`
  ${fontSizesMixinsR4.h5()};
  font-weight: ${fontWeights.bold};
  margin-top: 0;
  margin-bottom: 4px;
  transition: ${transition.fast};
  color: ${props => (props.isActive ? paletteR4.blue.base : paletteR4.black.base)};
`;

const StyledSubMenuItem = styled.a<{hasIcon: boolean; hoverBg?: string}>`
  padding: ${props => (props.hasIcon ? '12px 24px 20px 10px' : '12px 24px 20px 16px')};
  border-radius: 10px;
  text-decoration: none;
  display: flex;
  transition: ${transition.fast};

  &:hover {
    background: ${props => props.hoverBg || paletteR4.gray.highlight};
  }

  &:not(:first-child) {
    margin-top: 8px;
  }
`;

const StyledSubMenuItemDesc = styled.p`
  ${fontSizesMixinsR4.p4()};
  color: ${paletteR4.black.base};
  margin: 0;
`;

const StyledSubMenuItemContent = styled.div`
  width: 230px;
`;

const StyledSubMenuItemIcon = styled(Icon)`
  margin-right: 24px;
  margin-top: 7px;
  flex-shrink: 0;
`;

export const HeaderSubMenu: FC<HeaderSubMenuProps> = props => {
  const {items: categories} = props.subMenu;

  return (
    <StyledSubMenuWrapper $css={props.wrapperCss} singleCategory={categories.length === 1}>
      {categories.map(category => (
        <StyledSubCategory key={category.name}>
          <StyledSubCategoryTitle>{category.name}</StyledSubCategoryTitle>

          {category.items.map(item => (
            <StyledSubMenuItem
              key={item.name + item.href}
              href={item.href}
              hasIcon={!!item.iconName}
              hoverBg={item.hoverBg}
            >
              {item.iconName && (
                <StyledSubMenuItemIcon useR4 name={item.iconName as IconProps['name']} size={40} />
              )}
              <StyledSubMenuItemContent>
                <StyledSubMenuItemTitle isActive={item.href === props.locationPath}>
                  {item.name}
                </StyledSubMenuItemTitle>
                <StyledSubMenuItemDesc>{item.description}</StyledSubMenuItemDesc>
              </StyledSubMenuItemContent>
            </StyledSubMenuItem>
          ))}
        </StyledSubCategory>
      ))}
    </StyledSubMenuWrapper>
  );
};
